<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import Card from 'primevue/card';
import Select from 'primevue/select';
import Button from 'primevue/button';
import AddProductDialog from "./AddProductDialog.vue";

const props = defineProps({
    productOptions: {
        type: Array as () => string[],
        required: true,
    },
    selectedProduct: {
        type: String,
        required: true,
    },
    loadingProducts: {
        type: Boolean,
        default: false,
    },
    selectedLanguagesCount: {
        type: Number,
        default: 0,
    },
    hasItems: {
        type: Boolean,
        default: false,
    },
    loading: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    "product-change",
    "import",
    "export",
    "products-updated",
    "product-created"
]);

const { t } = useI18n();

// Add Product Dialog
const showAddProductDialog = ref(false);

const handleProductChange = (event: any) => {
    emit("product-change", event.value);
};

const handleAddProduct = () => {
    showAddProductDialog.value = true;
};

const handleImport = () => {
    // Directly emit import event to trigger CSV import dialog
    emit("import");
};

const handleExport = () => {
    emit("export");
};
</script>

<template>
    <!-- Header Card with Product Selection, Add Product, and Import/Export -->
    <Card class="mb-6">
        <template #content>
            <div class="grid grid-cols-12 gap-4">
                <!-- Product Selection -->
                <div class="col-span-12 md:col-span-3">
                    <label for="text-maintenance_product-select" class="block text-sm font-medium mb-2">
                        {{ t("textMaintenance.product") }}
                    </label>
                    <Select
                        id="text-maintenance_product-select"
                        :model-value="selectedProduct"
                        :options="productOptions"
                        :placeholder="t('textMaintenance.selectProduct')"
                        :loading="loadingProducts"
                        filter
                        class="w-full"
                        @change="handleProductChange"
                        data-cy="text-maintenance_product-selector"
                    />
                </div>

                <div class="col-span-12 md:col-span-3 flex items-end">
                    <Button 
                        @click="handleAddProduct" 
                        :label="t('textMaintenance.addProduct')"
                        icon="pi pi-plus"
                        severity="secondary"
                        class="add-product-btn"
                        v-tooltip.top="t('textMaintenance.addProduct')"
                        data-cy="text-maintenance_add-product-button"
                    />
                </div>

                <!-- Import/Export Buttons -->
                <div class="col-span-12 md:col-span-6">
                    <div class="block text-sm font-medium mb-2 opacity-0">
                        {{ t("textMaintenance.actions") }}
                    </div>
                    <div class="flex justify-end gap-2">
                        <Button
                            :label="t('textMaintenance.import')"
                            icon="pi pi-download"
                            severity="secondary"
                            @click="handleImport"
                            :disabled="!selectedProduct"
                            :data-cy="'text-maintenance_import-button'"
                        />
                        <Button
                            :label="t('textMaintenance.export')"
                            icon="pi pi-upload"
                            @click="handleExport"
                            :disabled="!selectedProduct || !hasItems"
                            :data-cy="'text-maintenance_export-button'"
                        />
                    </div>
                </div>
            </div>
        </template>
    </Card>

    <!-- Add Product Dialog -->
    <AddProductDialog
        v-model:visible="showAddProductDialog"
        @products-updated="$emit('products-updated')"
        @product-created="$emit('product-created', $event)"
    />
</template>

<style scoped>
.p-card {
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.add-product-btn {
    width: 100%;
    max-width: 200px;
}

@media (max-width: 1168px) {
    .add-product-btn :deep(.p-button-label) {
        display: none;
    }
    
    .add-product-btn {
        width: auto;
        min-width: 2.5rem;
        justify-content: center;
    }
}

@media (min-width: 1169px) {
    .add-product-btn {
        width: 100%;
        max-width: 250px;
    }
}
</style>